/**
 * Store de notificações do app (lembretes, PDFs gerados, etc.).
 * Persistido em localStorage. Extensível para futuras origens.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AppNotification, NotificationData, NotificationSource } from './notificationTypes'

const STORAGE_KEY = 'synap-notifications'

function generateId(): string {
  return `n_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
}

interface NotificationsState {
  items: AppNotification[]
  add: (source: NotificationSource, title: string, body: string | undefined, data: NotificationData, read?: boolean) => AppNotification
  markAsRead: (id: string) => void
  markAllAsRead: () => void
  remove: (id: string) => void
  clearAll: () => void
}

export const useNotificationsStore = create<NotificationsState>()(
  persist(
    (set) => ({
      items: [],

      add: (source, title, body, data, read = false) => {
        const item: AppNotification = {
          id: generateId(),
          source,
          title,
          body,
          read,
          createdAt: new Date().toISOString(),
          data,
        }
        set((state) => ({
          items: [item, ...state.items],
        }))
        return item
      },

      markAsRead: (id) =>
        set((state) => ({
          items: state.items.map((n) => (n.id === id ? { ...n, read: true } : n)),
        })),

      markAllAsRead: () =>
        set((state) => ({
          items: state.items.map((n) => ({ ...n, read: true })),
        })),

      remove: (id) =>
        set((state) => ({
          items: state.items.filter((n) => n.id !== id),
        })),

      clearAll: () => set({ items: [] }),
    }),
    { name: STORAGE_KEY, version: 1 },
  ),
)
